import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config/api';

/**
 * AIUsagePanel — the "AI Usage" tab in User Management.
 *
 * Reports Suggestions spend for the organization, broken out per user. This
 * is tracked separately from the assistant on purpose: the assistant is
 * interactive and user-paced, while Suggestions is a per-candidate batch
 * whose cost scales with pipeline size. One combined number would hide
 * which of the two is actually driving spend.
 *
 * The acceptance rate is the number worth watching. Spend with a low accept
 * rate means the model is proposing noise and the prompt needs tightening —
 * cost alone doesn't tell you whether the feature is earning its keep.
 */

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const compact = (n) => (n || 0).toLocaleString();

const AIUsagePanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [days, setDays] = useState(30);
  const [caps, setCaps] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/candidates/suggestions/usage?days=${days}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } }
      );
      if (res.status === 403) throw new Error('You do not have permission to view organization usage.');
      if (!res.ok) throw new Error('Could not load usage.');
      const body = await res.json();
      setData(body);
      if (body.caps) {
        // These are the EFFECTIVE caps for this org (its own values clamped
        // by the platform ceiling), which is what actually gets enforced.
        setCaps({
          daily_cap_cents_user:  body.caps.dailyCapCentsUser,
          daily_cap_cents_org:   body.caps.dailyCapCentsOrg,
          monthly_cap_cents_org: body.caps.monthlyCapCentsOrg,
        });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);


  const allUncapped = caps
    && !caps.daily_cap_cents_user && !caps.daily_cap_cents_org && !caps.monthly_cap_cents_org;
  const outcomes = data?.outcomes || { pending: 0, accepted: 0, dismissed: 0 };
  const resolved = outcomes.accepted + outcomes.dismissed;
  const acceptRate = resolved > 0 ? Math.round((outcomes.accepted / resolved) * 100) : null;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">AI Usage — Suggestions</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Cost of note-scanning runs across your organization. Tracked separately from the AI assistant.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="px-2 py-1.5 text-sm border rounded-md"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            onClick={load}
            className="px-3 py-1.5 text-sm border rounded-md text-gray-600 hover:bg-gray-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto" />
        </div>
      ) : !data ? null : (
        <>
          {/* Totals */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Today', value: money(data.totals.today_cents) },
              { label: 'This month', value: money(data.totals.month_cents) },
              { label: `Last ${data.window_days} days`, value: money(data.totals.window_cents) },
              { label: 'Scans run', value: compact(data.totals.runs) },
            ].map(card => (
              <div key={card.label} className="bg-white rounded-lg border p-3">
                <p className="text-xs text-gray-500">{card.label}</p>
                <p className="text-xl font-semibold text-gray-900 mt-0.5">{card.value}</p>
              </div>
            ))}
          </div>

          {/* Outcome quality */}
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900">Suggestion outcomes</h3>
              {acceptRate !== null && (
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                  acceptRate >= 50 ? 'bg-green-100 text-green-700'
                    : acceptRate >= 25 ? 'bg-amber-100 text-amber-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {acceptRate}% accepted
                </span>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-lg font-semibold text-green-700">{compact(outcomes.accepted)}</p>
                <p className="text-xs text-gray-500">Applied</p>
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-500">{compact(outcomes.dismissed)}</p>
                <p className="text-xs text-gray-500">Dismissed</p>
              </div>
              <div>
                <p className="text-lg font-semibold text-amber-600">{compact(outcomes.pending)}</p>
                <p className="text-xs text-gray-500">Awaiting review</p>
              </div>
            </div>
            {acceptRate !== null && acceptRate < 25 && (
              <p className="text-xs text-gray-500 mt-3 pt-3 border-t">
                A low acceptance rate usually means the model is proposing changes the notes don't
                really support. Worth reviewing a few dismissed suggestions before increasing usage.
              </p>
            )}
          </div>

          {/* Observed economics — the evidence for choosing cap numbers.
              Deliberately sits directly above the cap inputs: the busiest-day
              figure is the one that matters, since a cap sized to the average
              will fire on every genuinely busy day. */}
          <div className="bg-white rounded-lg border p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">What a scan actually costs</h3>
            <p className="text-xs text-gray-500 mb-3">
              Observed over the last {data.window_days} days. Use these to size the limits below.
            </p>
            {data.per_scan.total_runs === 0 ? (
              <p className="text-sm text-gray-500 py-2">
                No scans recorded yet — run the feature for a week before setting limits, otherwise you're guessing.
              </p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <p className="text-xs text-gray-500">Average per scan</p>
                  <p className="text-lg font-semibold text-gray-900 tabular-nums">
                    {data.per_scan.avg_cents !== null ? money(data.per_scan.avg_cents) : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Busiest day (org)</p>
                  <p className="text-lg font-semibold text-gray-900 tabular-nums">{money(data.per_scan.busiest_day_cents)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Busiest day (one user)</p>
                  <p className="text-lg font-semibold text-gray-900 tabular-nums">{money(data.per_scan.busiest_user_day_cents)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Most scans in a day</p>
                  <p className="text-lg font-semibold text-gray-900 tabular-nums">{compact(data.per_scan.busiest_day_runs)}</p>
                </div>
              </div>
            )}
          </div>

          {/* Spend limits are GLOBAL (stored without a firm column), so they're
              edited in Settings → System by a system administrator, not here.
              Shown read-only so an org admin can still see what they're
              working against. */}
          {caps && (
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Spend limits</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Set under Settings → Organization, within the platform ceiling.
                  </p>
                </div>
                {allUncapped && (
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                    No limits set
                  </span>
                )}
              </div>
              {!allUncapped && (
                <div className="grid grid-cols-3 gap-3 mt-3 text-center">
                  <div>
                    <p className="text-sm font-semibold text-gray-900 tabular-nums">{money(caps.daily_cap_cents_user)}</p>
                    <p className="text-xs text-gray-500">Daily / user</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 tabular-nums">{money(caps.daily_cap_cents_org)}</p>
                    <p className="text-xs text-gray-500">Daily / org</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 tabular-nums">{money(caps.monthly_cap_cents_org)}</p>
                    <p className="text-xs text-gray-500">Monthly / org</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Per organization — the accounting view. Every line carries the
              org name so spend can be attributed without a lookup. */}
          {data.organizations && data.organizations.length > 0 && (
            <div className="bg-white rounded-lg border overflow-hidden">
              <div className="px-4 py-2.5 border-b bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-900">By organization</h3>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b">
                    <th className="px-4 py-2 font-medium">Organization</th>
                    <th className="px-4 py-2 font-medium text-right">Users</th>
                    <th className="px-4 py-2 font-medium text-right">Scans</th>
                    <th className="px-4 py-2 font-medium text-right">Today</th>
                    <th className="px-4 py-2 font-medium text-right">This month</th>
                    <th className="px-4 py-2 font-medium text-right">Period</th>
                  </tr>
                </thead>
                <tbody>
                  {data.organizations.map(o => (
                    <tr key={o.firm_id || 'none'} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium text-gray-900">{o.organization}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-500">{compact(o.users)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-700">{compact(o.runs)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-700">{money(o.today_cents)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-700">{money(o.month_cents)}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-medium text-gray-900">{money(o.window_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Per user */}
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="px-4 py-2.5 border-b bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-900">By user</h3>
            </div>
            {data.users.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-sm text-gray-500">No Suggestions usage in this period.</p>
                <p className="text-xs text-gray-400 mt-1">
                  Usage appears here once someone runs a note scan from a candidate profile.
                </p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b">
                    <th className="px-4 py-2 font-medium">User</th>
                    <th className="px-4 py-2 font-medium">Organization</th>
                    <th className="px-4 py-2 font-medium text-right">Scans</th>
                    <th className="px-4 py-2 font-medium text-right">Suggestions</th>
                    <th className="px-4 py-2 font-medium text-right">Tokens</th>
                    <th className="px-4 py-2 font-medium text-right">Cost</th>
                    <th className="px-4 py-2 font-medium text-right">Last used</th>
                  </tr>
                </thead>
                <tbody>
                  {data.users.map(u => (
                    <tr key={u.user_id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <p className="font-medium text-gray-900">{u.user_name || `User ${u.user_id}`}</p>
                        {u.user_email && <p className="text-xs text-gray-500">{u.user_email}</p>}
                      </td>
                      <td className="px-4 py-2">
                        <span className="inline-block px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-700">
                          {u.organization || 'Unassigned'}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right text-gray-700 tabular-nums">{compact(u.runs)}</td>
                      <td className="px-4 py-2 text-right text-gray-700 tabular-nums">{compact(u.suggestions)}</td>
                      <td className="px-4 py-2 text-right text-gray-500 tabular-nums">{compact(u.tokens)}</td>
                      <td className="px-4 py-2 text-right font-medium text-gray-900 tabular-nums">{money(u.window_cents)}</td>
                      <td className="px-4 py-2 text-right text-xs text-gray-500">
                        {u.last_used ? new Date(u.last_used).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <p className="text-xs text-gray-400">
            Costs are estimates from token counts at published model pricing, not billed amounts.
          </p>
        </>
      )}
    </div>
  );
};

export default AIUsagePanel;
